import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Typography, Chip, Tooltip } from "@mui/material";
import { Button, CircularProgress } from "@deere/fuel-react";
import ToastMessageFunction from "../../common/toast-message-function";
import { getSuccessfulTransactions } from "./utils/api";
import { useSelector } from "react-redux";
import TransactionsTable from "../common/TransactionsTable";
import SearchFilters from "./components/SearchFilters";
import InvoiceNumberCell from "../common/InvoiceNumberCell";
import { Check, Cancel } from "@mui/icons-material";
import { exportColumns } from "../accountingApproval/utils/constants";
const SucessfulTransactions = () => {
  const user = useSelector((state) => state.userState.userData);
  const toastManager = ToastMessageFunction();
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    limit: 500, // Reduced from 500 for faster loading
    offset: 0,
    sortBy: "billingDate",
    sortOrder: "DESC",
  });
  const [dateRange, setDateRange] = useState({ start: null, end: null });

  const showToast = useCallback(
      (message, severity) => {
        toastManager.getToast(message, severity);
      },
      [toastManager]
    );

  const fetchTransactions = async (filterParams = {}) => {
      setIsLoading(true);
      try {
        const mergedFilters = { ...filters, ...filterParams };
        
        // Convert dayjs dates to ISO format if they exist
        if (mergedFilters.startDate && mergedFilters.startDate.$isDayjsObject) {
          mergedFilters.startDate = mergedFilters.startDate.format('YYYY-MM-DD');
        }
        if (mergedFilters.endDate && mergedFilters.endDate.$isDayjsObject) {
          mergedFilters.endDate = mergedFilters.endDate.format('YYYY-MM-DD');
        }
        
        const response = await getSuccessfulTransactions(
          user,
          "SUCCESS",
          mergedFilters
        );
        
        if (response && response.success) {
          const mappedData = response.data.map((item, index) => {
            
            let memoRequest = null;
            let requestItems = [];
            let amount = 0;
            let currency = "";
            let material = "";
  
            // Determine if the item is a debit or credit memo request
            if (item.debitMemoRequest) {
              memoRequest = { ...item.debitMemoRequest };
              requestItems = memoRequest.debitMemoRequestItem || [];
            } else if (item.creditMemoRequest) {
              memoRequest = { ...item.creditMemoRequest };
              requestItems = memoRequest.creditMemoRequestItem || [];
            }
  
            // Extract amount, currency, and material from request items
            if (requestItems.length > 0) {
              const conditions = requestItems[0][ item.debitMemoRequest ? "debitMemoRequestItemConditions" : "creditMemoRequestItemConditions"] || [];
              amount = conditions.length > 0 ? conditions[0]?.condValue || 0 : 0;
              currency =  conditions.length > 0 ? conditions[0]?.currency || "" : "";
              material = requestItems[0]?.material || "";
            }
  
            const itemTextString = requestItems.length > 0
                ? requestItems[0].itemText.join("\r\n")
                : "";
  
            return {
              ...memoRequest,
              billingDataId: item.billingDataId,
              source: item.billingDataId ? "UPLOAD" : "API",
              dealerNumber: item.dealerNumber,
              dealerUnit: item.dealerUnit,
              dealerName: item.dealerName,
              amount: amount,
              totalAmount: item.totalAmount || (amount && requestItems.length > 0 ? amount * (requestItems[0]?.targetQty || 1) : 0),
              currencydisplay: currency,
              material: material,
              itemText: itemTextString,
              transactionType: item.debitMemoRequest ? "Debit" : "Credit",
              isSapNumberUploaded:item.sapNumberUploaded,
              sapNumber:item.sapNumber,
              accountingStatus: item.accountingStatus,
              reasonOfRejection: item.reasonOfRejection,
              userId: item.userId || "",
              accountingUserId: item.accountingUserId,
              accountingApprovalTimestamp: item.accountingApprovalTimestamp,
            };
          });
          
          setTransactions(mappedData);
          setFilteredTransactions(mappedData);
          
          // Calculate actual date range from returned data ONLY if no date filters were applied
          const hasDateFilters = mergedFilters.startDate || mergedFilters.endDate;
          if (!hasDateFilters && mappedData.length > 0) {
            
            const dates = mappedData
              .map(t => t.billingDate)
              .filter(d => d)
              .sort();
            if (dates.length > 0) {
              setDateRange({
                start: dates[0],
                end: dates[dates.length - 1]
              });
            }
          }
        } else {
          showToast(response.message || "Failed to fetch transactions", "error");
        }
      } catch (error) {
        showToast(error.message || "An error occurred", "error");
      } finally {
        setIsLoading(false);
      }
    };  const handleSearch = (searchFilters) => {
    // If date filters are applied, remove the 500 limit to get all matching records
    const hasDateFilter = searchFilters.startDate || searchFilters.endDate;
    
    const newFilters = {
      ...filters,
      limit: hasDateFilter ? 999999 : 500, // No limit when date filter is applied
      offset: 0, // Reset to first page
      startDate: searchFilters.startDate,
      endDate: searchFilters.endDate,
      dealerNumber: searchFilters.dealerNumber,
      dealerCountry: searchFilters.dealerCountry,
      dealerUnit: searchFilters.dealerUnit,
      source: searchFilters.source,
    };
    setFilters(newFilters);
    
    // Update date range display with applied filters if date filters are provided
    if (searchFilters.startDate || searchFilters.endDate) {
      setDateRange({
        start: searchFilters.startDate && searchFilters.startDate.$isDayjsObject 
          ? searchFilters.startDate.format('YYYY-MM-DD') 
          : searchFilters.startDate,
        end: searchFilters.endDate && searchFilters.endDate.$isDayjsObject 
          ? searchFilters.endDate.format('YYYY-MM-DD') 
          : searchFilters.endDate
      });
    }
    
    fetchTransactions(newFilters);
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      limit: 500,
      offset: 0,
      sortBy: "billingDate",
      sortOrder: "DESC",
      startDate: null,
      endDate: null,
      dealerNumber: null,
      dealerCountry: null,
      dealerUnit: null,
      source: null,
    };
    setFilters(defaultFilters);
    setDateRange({ start: null, end: null }); // Reset date range display
    fetchTransactions(defaultFilters);
  };

  const handleLoadMore = () => {
    const newFilters = {
      ...filters,
      offset: filters.offset + filters.limit,
    };
    setFilters(newFilters);
    fetchTransactions(newFilters);
  };

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dealerUnits = useMemo(() => {
    return Array.from(new Set(transactions.map((t) => t.dealerUnit).filter(Boolean)));
  }, [transactions]);

  const dealerCountries = useMemo(() => {
    return Array.from(new Set(transactions.map((t) => t.taxDestinationCountry).filter(Boolean)));
  }, [transactions]);

  const columns = [

    { 
      header: "Billing Date", 
      accessorKey: "billingDate", 
      size: 120,
      sortingFn: 'datetime',
      filterFn: (row, columnId, filterValue) => {
        const date = row.getValue(columnId);
        if (!date) return false;
        const [year, month, day] = date.split('-');
        return `${day}-${month}-${year}`.includes(filterValue);
      },
      Cell: ({ cell }) => {
        const date = cell.getValue();
        if (!date) return '';
        // Convert YYYY-MM-DD to DD-MM-YYYY
        const [year, month, day] = date.split('-');
        return `${day}-${month}-${year}`;
      },
    },
    {
      header: "Dealer Number",
      accessorKey: "dealerNumber",
      size: 130,
      Cell: ({ row }) => {
        const { dealerNumber, dealerName, sapNumber, isSapNumberUploaded, source } = row.original;
        const displayValue = isSapNumberUploaded && sapNumber && source === "UPLOAD" ? sapNumber : dealerNumber;
        return dealerName ? (
          <Tooltip title={dealerName}>
            <span style={{ cursor: "default" }}>{displayValue}</span>
          </Tooltip>
        ) : (
          <span>{displayValue}</span>
        );
      },
    },
    {
      header: "Dealer Country",
      accessorKey: "taxDestinationCountry",
      size: 130,
    },
    {
      header: "Material",
      accessorKey: "material",
      size: 110,
    },
    {
      header: "Total Amount",
      accessorKey: "totalAmount",
      size: 110,
      muiTableBodyCellProps: {
        sx: { 
          textAlign: 'right',
          paddingLeft: 0,
          paddingRight: 0,
          borderBottom: '1px solid rgb(224, 224, 224)'
        }
      },
      muiTableHeadCellProps: {
        sx: { textAlign: 'right', paddingLeft: 0, paddingRight: 0 }
      },
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (value == null) return '';
        const formatted = Number(value).toFixed(2);
        return formatted.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');
      },
    },
    {
      header: "Currency",
      accessorKey: "currencydisplay",
      size: 100,
      muiTableBodyCellProps: {
        sx: { textAlign: 'center', borderBottom: '1px solid rgb(224, 224, 224)' }
      },
      muiTableHeadCellProps: {
        sx: { textAlign: 'center' }
      },
    },
    {
      header: "Item Text",
      accessorKey: "itemText",
      size: 500,
      Cell: ({ renderedCellValue }) => {
        const itemText = renderedCellValue || '';
        
        return (
          <Box sx={{ 
            maxWidth: '900px',
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: '0.875rem',
                lineHeight: 1.4,
              }}
            >
              {itemText}
            </Typography>
          </Box>
        );
      },
    },
    {
      header: "Request Number",
      accessorKey: "requestNumber",
      size: 140,
    },
    {
      header: "Invoice Number",
      accessorKey: "invoiceNumber",
      size: 140,
      Cell: ({ row }) => {
        const { invoiceNumber } = row.original;
        return (
          <InvoiceNumberCell 
            invoiceNumber={invoiceNumber}
          />
        );
      },
    },
    {
      header: "Account Number",
      accessorKey: "accountNumber",
      size: 140,
    },
    {
      header: "Source",
      size: 100,
      accessorKey: "source",
    },
    {
      header: "Type",
      accessorKey: "transactionType",
      size: 100,
    }
    ,{
  header: "Accounting User",
  accessorKey: "accountingUserId",
  size: 180,
  Cell: ({ row }) => {
    const { accountingStatus, accountingUserId } = row.original;
    if (!accountingUserId || accountingStatus === "NOT_APPLICABLE") return "-";
    return accountingUserId;
  },
},
{
  header: "Accounting Status",
  accessorKey: "accountingStatus",
  filterVariant: "select",
  filterSelectOptions: ["APPROVED", "REJECTED","NOT_APPLICABLE"],
  size: 160,
  Cell: ({ row }) => {
    const { accountingStatus, reasonOfRejection } = row.original;
    if (accountingStatus === "APPROVED") {
      return <Chip label="Approved" color="success" icon={<Check />} />;
    }
    if (accountingStatus === "REJECTED") {
      return (
        <Tooltip title={reasonOfRejection || "Rejected"}>
          <Chip label="Rejected" color="error" icon={<Cancel />} />
        </Tooltip>
      );
    }
    if (accountingStatus === "NOT_APPLICABLE") {
      return <Chip label="Not Applicable" sx={{ backgroundColor: "#e0e0e0", color: "#616161" }} />;
    }
    return null;
  },
},
{
  header: "Approval Timestamp",
  accessorKey: "accountingApprovalTimestamp",
  size: 180,
  filterFn: (row, columnId, filterValue) => {
    const ts = row.getValue(columnId);
    const accountingStatus = row.original.accountingStatus;
    if (accountingStatus === 'NOT_APPLICABLE' || !ts) return false;
    const d = new Date(ts);
    const formatted = `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`;
    return formatted.includes(filterValue);
  },
  Cell: ({ row }) => {
    const { accountingStatus, accountingApprovalTimestamp } = row.original;
    if (accountingStatus === "NOT_APPLICABLE" || !accountingApprovalTimestamp) return "-";
    
    // Format date as dd-mm-yyyy
    const date = new Date(accountingApprovalTimestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
  },
},
  ];

  useEffect(() => {
      setFilteredTransactions(transactions);
    }, [transactions]);

  return (
    <Box>
      {toastManager.toast}
      
      {/* Search Filters - Keep outside loading conditional to maintain state */}
      <SearchFilters 
        onSearch={handleSearch}
        onClear={handleClearFilters}
        dealerUnits={dealerUnits}
        dealerCountries={dealerCountries}
        user={user}
      />
      
      {isLoading ? (
        <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", height: "50vh"}}>
          <CircularProgress aria-label={"Loading..."} data-fuel-size={"medium"}/>
        </Box>
      ) : (
        <div>
          {/* Date Range Display */}
          {dateRange.start && dateRange.end && (
            <Box
              sx={{
                padding: "10px 20px",
                backgroundColor: "#f5f5f5",
                marginBottom: "10px",
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" sx={{ color: "#666" }}>
                Billing date range: <strong>{dateRange.start}</strong> to <strong>{dateRange.end}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                Total: <strong>{transactions.length}</strong> records
              </Typography>
            </Box>
          )}

          {/* material table for successful transactions */}
          <TransactionsTable 
          columns={columns}
          filteredTransactions={filteredTransactions}
          fetchTransactions={fetchTransactions}
          showToast={showToast}
          initialSorting={[{ id: 'billingDate', desc: true }]}
          disableRowSelection={true}  
          exportConfig={exportColumns}
          />
          
          {/* Load More Button */}
          {transactions.length === filters.limit && (
            <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
              <Button
                variant="primary"
                onClick={handleLoadMore}
              >
                Load More
              </Button>
            </Box>
          )}
        </div>
      )}
    </Box>
  );
};

export default SucessfulTransactions;
